// @nexus/shared/rbac — single source of truth for the Location RBAC.

import { hasAnyPermission, type Permission } from "./permissions.js";

// 角色 / 權限模型（PR #27 凍結；對應 infra/RBAC.md）
// -----------------------------------------------------
// 5 個 tenant 角色：
//   - tenant_admin       — 該租戶全權限：使用者、案場、班表、所有報表/事件、計費
//   - manager            — 案場主管：管轄案場的事件、巡邏、班表編排、報表、人員查看
//   - officer            — 巡邏員：自己的任務/班表/巡邏歷史、回報異常、查當班案場
//   - incident_reporter  — 通報員：提交異常 + 查自己提交的異常狀態
//   - auditor            — 稽核員（唯讀）：所有報表/事件/巡邏記錄唯讀
//
// 平台角色 platform_admin 仍存在於 nexus_control.platform_users，但**不**屬於本
// 租戶 RBAC 模型。Gateway 處理 platform_admin 時走 impersonation 路徑。
//
// guest 是「LINE 登入但尚未綁租戶」的暫態，可看公開資源（liff exchange 會
// 給最小 scope），不在 ROLES catalog 內。
//
// 所有 endpoint 必須宣告需要的 scope。Gateway middleware 與每個 sub-API 都
// 會檢查 (defense-in-depth)。沒宣告 scope 的 protected route 會被 build-time
// audit script 擋下。

// ─── Scope catalog (canonical) ───────────────────────────────────────────

/** 完整 scope 列舉。新增 scope 時 **必須** 同步更新 ROLE_SCOPES。 */
export const SCOPES = [
  // events / incidents
  "read:events", "write:events", "delete:events",
  "read:incident", "write:incident", "ack:incident",
  "read:incident:self",
  // patrol
  "read:patrol", "write:patrol", "read:patrol:self",
  // schedule
  "read:schedule", "write:schedule", "read:schedule:self",
  // reports
  "read:report:officer", "read:report:site", "read:report:tenant",
  // sites / locations / routes
  "read:site", "write:site",
  // users / memberships
  "read:user", "write:user",
  // self-only — every logged-in role gets these so LIFF /v1/me/* (settings,
  // notifications) works without granting cross-user reads.
  "read:user:self", "write:user:self",
  // billing
  "read:billing", "write:billing",
  // notifications
  "read:notification", "write:notification",
  // tenant settings
  "read:tenant", "write:tenant",
  // line bindings
  "bind:line",
  // audit log
  "read:audit",
  // tenant admin meta-scope (wildcard within one tenant)
  "admin:tenant",
  // ─── Platform-level (super_admin only) ───────────────────────────────
  // Cross-tenant directory CRUD.
  "read:tenants", "write:tenants",
  // Switch the acting tenant via JWT claim `acting_tenant_id`.
  "switch:tenant",
  // Platform wildcard — broader than admin:tenant; passes every scope check.
  "admin:platform",
  // Shared Skill Layer management — super_admin only. Spec
  // docs/specs/shared-skill-layer.md §8 + §15. admin:shared_skills 是常規
  // 管理權限；:debug 是內部稽核才解鎖的 candidate 原始來源檢視。
  "admin:shared_skills",
  "admin:shared_skills:debug",
] as const;

export type Scope = typeof SCOPES[number];

const SCOPE_SET: Set<string> = new Set(SCOPES);

export function isScope(s: string): s is Scope {
  return SCOPE_SET.has(s);
}

// ─── Role catalog ────────────────────────────────────────────────────────

export const ROLES = [
  // tenant-scoped — original 5
  "tenant_admin",
  "manager",
  "officer",
  "incident_reporter",
  "auditor",
  // tenant-scoped — Floor Stack Phase C (added 2026-05)
  // - site_supervisor:         案場督導，介於 manager 與 officer 之間
  // - control_center_operator: 中控員，read-heavy + ack incident
  // - client_viewer:           外部客戶，只看自己 client 的報表
  "site_supervisor",
  "control_center_operator",
  "client_viewer",
  // platform-scoped (1) — cross-tenant SENTRY staff
  "super_admin",
] as const;

export type Role = typeof ROLES[number];

const ROLE_SET: Set<string> = new Set(ROLES);

export function isRole(s: string): s is Role {
  return ROLE_SET.has(s);
}

/** Roles whose user can be enrolled as a 執勤者 — face + WebAuthn
 *  Passkey registered, `officers` row auto-created on LINE bind /
 *  role assign, eligible for `officer_site_assignments` + patrol
 *  tasks. Before 2026-05 this was only officer + site_supervisor;
 *  operator feedback broadened to "every tenant-scoped role except
 *  super_admin can patrol when staffing is thin" — including admin,
 *  manager, incident_reporter, auditor, control_center_operator,
 *  client_viewer. Server-side gates (auth-api/binding-redeem.ts,
 *  tenant-api/admin-users.ts) consult this set when deciding
 *  whether to upsert the officers row. */
export const ENROLLABLE_ROLES = Object.freeze([
  "tenant_admin",
  "manager",
  "site_supervisor",
  "officer",
  "incident_reporter",
  "auditor",
  "control_center_operator",
  "client_viewer",
] as const);

const ENROLLABLE_ROLE_SET: Set<string> = new Set(ENROLLABLE_ROLES);

export function isEnrollableRole(s: string): s is (typeof ENROLLABLE_ROLES)[number] {
  return ENROLLABLE_ROLE_SET.has(s);
}

// ─── Legacy role mapping (nexus_ops_s00 既有資料) ───────────────────────────
//
// 現有 schema 的 role 字串 → 規範角色：
//   owner / admin / platform_admin → tenant_admin
//   manager → manager
//   viewer  → auditor
//   officer → officer

const LEGACY_MAP: Record<string, Role> = {
  owner: "tenant_admin",
  admin: "tenant_admin",
  manager: "manager",
  viewer: "auditor",
  officer: "officer",
  platform_admin: "tenant_admin",
};

/** Normalise a legacy / canonical role string → canonical Role | null.
 *  Returns null for unknown / "guest" / empty input. */
export function normaliseRole(input: string | null | undefined): Role | null {
  if (!input) return null;
  if (isRole(input)) return input;
  return LEGACY_MAP[input] ?? null;
}


// ─── Per-LIFF-app role allowlist ─────────────────────────────────────────
//
// site_live      → tenant_admin / manager / officer / auditor
// incident       → 全部
// event          → tenant_admin / manager / auditor
// manager_today  → tenant_admin / manager
// officer_tasks  → officer / tenant_admin (可代查)
// report         → tenant_admin / manager / auditor

// Mirrors infra/line/rich-menu/endpoints.json. super_admin is added to
// every entry: a platform admin can impersonate every page (the actual
// data layer still scopes to whichever tenant they're switched into).
// Adding a new endpoint requires extending this map — the LIFF exchange
// handler refuses any liff_app not listed here with `forbidden_for_role`.
export const LIFF_APP_ROLE_ALLOW: Readonly<Record<string, readonly Role[]>> = Object.freeze({
  // Legacy 6 page-kind LIFFs
  site_live:     ["tenant_admin", "manager", "officer", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  incident:      ["tenant_admin", "manager", "officer", "incident_reporter", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  event:         ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  manager_today: ["tenant_admin", "manager", "site_supervisor", "super_admin"],
  officer_tasks: ["officer", "tenant_admin", "super_admin"],
  report:        ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  // 'binding' — guest landing page where a new follower redeems an invite
  // code. Open to every RBAC role so existing users with an obsolete
  // binding can also re-bind.
  binding:       ["tenant_admin", "manager", "officer", "incident_reporter", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],

  // Patrol family
  patrol_today:               ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  patrol_live:                ["tenant_admin", "manager", "site_supervisor", "control_center_operator", "super_admin"],
  patrol_scan:                ["officer", "tenant_admin", "super_admin"],

  // Incident submission flows (distinct from legacy "incident" picker)
  incident_new:               ["tenant_admin", "manager", "officer", "incident_reporter", "site_supervisor", "control_center_operator", "super_admin"],
  incident_my:                ["incident_reporter", "officer", "manager", "tenant_admin", "super_admin"],

  // Reports sub-routes
  reports_site:               ["tenant_admin", "manager", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  reports_audit:              ["tenant_admin", "auditor", "super_admin"],

  // Schedule / history per role
  manager_schedule:           ["tenant_admin", "manager", "site_supervisor", "super_admin"],
  officer_schedule:           ["officer", "tenant_admin", "super_admin"],
  officer_history:            ["officer", "tenant_admin", "super_admin"],

  // Auditor-only
  audit_patrol:               ["auditor", "tenant_admin", "super_admin"],
  audit_log:                  ["auditor", "tenant_admin", "super_admin"],

  // Tenant admin only
  admin_users:                ["tenant_admin", "super_admin"],
  admin_invites:              ["tenant_admin", "super_admin"],

  // Generic /me — every authenticated role
  me:                         ["tenant_admin", "manager", "officer", "incident_reporter", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  me_settings:                ["tenant_admin", "manager", "officer", "incident_reporter", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],

  // Platform (super_admin) namespace
  platform_tenant_switcher:   ["super_admin"],
  platform_announcements:     ["super_admin"],
  platform_reports:           ["super_admin"],
  platform_health:            ["super_admin"],
  platform_users:             ["super_admin"],
});

export function isRoleAllowedOnLiffApp(role: Role, liffApp: string): boolean {
  const list = LIFF_APP_ROLE_ALLOW[liffApp];
  if (!list) return false;
  return list.includes(role);
}

// ─── Dashboard route allowlist ───────────────────────────────────────────
//
// 24 個 dashboard route 對應角色清單。Sidenav 過濾 + RequireRole guard 共用。

export const DASHBOARD_ROUTE_ROLES: Readonly<Record<string, readonly Role[]>> = Object.freeze({
  // Tenant-level dashboard (tenant_admin / manager / officer / auditor).
  // super_admin can also enter every tenant page (impersonation use-cases).
  "/dashboard":                    ["tenant_admin", "manager", "officer", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  "/dashboard/monitor":            ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  // `/dashboard/tenants` removed 2026-05+: super_admin uses
  // /dashboard/platform/tenants; tenant_admin edits org branding from
  // /dashboard/settings.
  "/dashboard/clients":            ["tenant_admin", "manager", "auditor", "site_supervisor", "client_viewer", "super_admin"],
  "/dashboard/sites":              ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  "/dashboard/locations":        ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  "/dashboard/routes":             ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  "/dashboard/tasks":              ["tenant_admin", "manager", "site_supervisor", "super_admin"],
  "/dashboard/submissions":        ["tenant_admin", "manager", "auditor", "site_supervisor", "super_admin"],
  "/dashboard/schedules":          ["tenant_admin", "manager", "officer", "auditor", "site_supervisor", "control_center_operator", "super_admin"],
  "/dashboard/reports":            ["tenant_admin", "manager", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  "/dashboard/line":               ["tenant_admin", "manager", "super_admin"],
  "/dashboard/users":              ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/devices":            ["tenant_admin", "manager", "super_admin"],
  "/dashboard/billing":            ["tenant_admin", "auditor", "super_admin"],
  "/dashboard/ai-usage":           ["tenant_admin", "auditor", "super_admin"],
  "/dashboard/audit":              ["tenant_admin", "auditor", "super_admin"],
  // Governed MCP tool-call human-approval inbox (spec:
  // docs/specs/nexus-mcp-approval-audit-v0.md). Gated by mcp:approve_tool_call,
  // a high-risk control-plane permission held by tenant_admin only by default;
  // super_admin god-mode also passes. The dashboard route is hidden by role in
  // the sidebar, but runtime-api re-checks the permission on every request.
  "/dashboard/mcp-approvals":      ["tenant_admin", "super_admin"],
  // Per-tenant MCP tool-visibility overrides — TIGHTEN-only control plane
  // (spec: docs/specs/nexus-mcp-tool-projection-policy-v0.md). Gated by
  // mcp:manage_projection, held by tenant_admin only by default; super_admin
  // god-mode also passes. runtime-api re-checks the permission on every call.
  "/dashboard/mcp-tool-projections": ["tenant_admin", "super_admin"],
  "/dashboard/admin/binding-codes":["tenant_admin", "manager", "auditor", "super_admin"],
  // Custom RBAC roles管理（spec: docs/specs/custom-rbac.md §6）— 看 list
  // 需 roles:read.list, 編輯 / 刪除 / 建立 需 roles:write.* — 但 dashboard
  // route gate 還是 role-based 直到 P9 verify-session 完全切換。tenant_admin
  // 是唯一能改 role 的角色；auditor 給 read-only 入口，manager 看不到。
  "/dashboard/admin/roles":         ["tenant_admin", "auditor", "super_admin"],
  "/dashboard/admin/roles/new":     ["tenant_admin", "super_admin"],
  "/dashboard/admin/roles/:id":     ["tenant_admin", "auditor", "super_admin"],
  // /settings is the per-user "個人設定" page (LINE binding, notifications,
  // future password change). Every role with a session can use it.
  "/dashboard/settings":           ["tenant_admin", "manager", "officer", "incident_reporter", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  // Platform-level (super_admin only).
  "/dashboard/platform":                          ["super_admin"],
  "/dashboard/platform/tenants":                  ["super_admin"],
  "/dashboard/platform/users":                    ["super_admin"],
  "/dashboard/platform/health":                   ["super_admin"],
  "/dashboard/platform/announcements":            ["super_admin"],
  "/dashboard/platform/me/link-line":             ["super_admin"],
  "/dashboard/platform/role-templates":           ["super_admin"],
  "/dashboard/platform/shared-skills":            ["super_admin"],
  "/dashboard/platform/shared-skills/candidates": ["super_admin"],
});

export function isRoleAllowedOnDashboardRoute(role: Role, route: string): boolean {
  const list = DASHBOARD_ROUTE_ROLES[route];
  if (!list) return false;
  return list.includes(role);
}

// ─── Dashboard route → required permissions（Custom RBAC §6）─────────────
//
// Permission-based replacement for DASHBOARD_ROUTE_ROLES. Each route maps
// to a list of permission ids; ANY one match grants access (matches the
// sub-API `requireAnyPermission` semantics). Empty list = any authenticated
// user (settings page, dashboard home).
//
// super_admin's god-mode permission set includes every permission, so they
// pass every gate. Frontend RequireRole / sidebar filter prefer this map
// over DASHBOARD_ROUTE_ROLES when both exist.

export const DASHBOARD_ROUTE_PERMISSIONS: Readonly<Record<string, readonly Permission[]>> = Object.freeze({
  // dashboard home — any authenticated user with any of the broad "read"
  // permissions (officer sees self.list / patrols:read.self; manager sees
  // sites:read.list etc.). Empty would also work; this is more explicit.
  "/dashboard":                    ["sites:read.list", "patrols:read.self", "patrols:read.list", "schedules:read.self", "incident:read.self", "ai:read.facts", "users:read.list"],
  "/dashboard/monitor":            ["sites:read.list", "patrols:read.list"],
  "/dashboard/clients":            ["clients:read.list"],
  "/dashboard/sites":              ["sites:read.list"],
  "/dashboard/locations":        ["locations:read.list"],
  "/dashboard/routes":             ["routes:read.list"],
  "/dashboard/tasks":              ["tasks:read.list"],
  "/dashboard/submissions":        ["tasks:read.list", "patrols:read.list"],
  "/dashboard/schedules":          ["schedules:read.list", "schedules:read.self"],
  "/dashboard/reports":            ["reports:read.tenant", "reports:read.site", "reports:read.officer"],
  "/dashboard/line":               ["line:execute.bind_group", "line:write.update_group"],
  "/dashboard/users":              ["users:read.list"],
  "/dashboard/devices":            ["users:read.officer_status"],
  "/dashboard/billing":            ["billing:read.invoices", "billing:read.usage"],
  "/dashboard/ai-usage":           ["billing:read.usage", "ai:read.usage"],
  "/dashboard/audit":              ["audit:read.log"],
  "/dashboard/mcp-approvals":      ["mcp:approve_tool_call"],
  "/dashboard/mcp-tool-projections": ["mcp:manage_projection"],
  "/dashboard/admin/binding-codes":["users:write.invite"],
  // Custom RBAC role management
  "/dashboard/admin/roles":         ["roles:read.list"],
  "/dashboard/admin/roles/new":     ["roles:write.create"],
  "/dashboard/admin/roles/:id":     ["roles:read.detail"],
  // /dashboard/settings — every authenticated user (LINE binding, notif
  // prefs, personal AI memory). Empty list = no permission gate; the
  // page itself uses actor.user_id for scoping.
  "/dashboard/settings":           [],
  // Platform-level — super_admin god mode satisfies the impersonate gate.
  "/dashboard/platform":                          ["platform:execute.impersonate"],
  "/dashboard/platform/tenants":                  ["platform:execute.impersonate"],
  "/dashboard/platform/users":                    ["platform:execute.impersonate"],
  "/dashboard/platform/health":                   ["platform:execute.impersonate"],
  "/dashboard/platform/announcements":            ["platform:execute.impersonate"],
  "/dashboard/platform/me/link-line":             ["platform:execute.impersonate"],
  "/dashboard/platform/role-templates":           ["platform:execute.impersonate"],
  "/dashboard/platform/shared-skills":            ["platform:execute.impersonate"],
  "/dashboard/platform/shared-skills/candidates": ["platform:execute.impersonate"],
});

/** Check if the actor's permission set grants access to `route`. Empty
 *  required list (e.g. /dashboard/settings) → any authenticated session
 *  passes. Unknown route → false (defensive default). */
export function actorCanAccessDashboardRoute(actorPermissions: readonly string[], route: string): boolean {
  const required = DASHBOARD_ROUTE_PERMISSIONS[route];
  if (!required) return false;
  if (required.length === 0) return true;
  return hasAnyPermission(actorPermissions as Permission[], [...required]);
}

/**
 * Roles that hold a given permission/scope, derived from the RBAC
 * source-of-truth route maps (NOT a hardcoded by-scope literal — NEXUS
 * CLAUDE.md §25 / FORGE §25). RBAC permission seeds are deferred to go-live /
 * the FORGE flow (NEXUS CLAUDE.md §9), so there is no static role→permission
 * table in code. The canonical, data-driven derivation is:
 *   1. find the dashboard route(s) whose required-permission list
 *      (`DASHBOARD_ROUTE_PERMISSIONS`) contains the scope, then
 *   2. union the roles allowed on those routes (`DASHBOARD_ROUTE_ROLES`).
 *
 * Because both maps are the existing source of truth that the dashboard +
 * runtime-api already gate on, anyone who later broadens (or narrows) which
 * roles can reach the gated route automatically changes this result — callers
 * that route notifications by "who can approve" follow the route gate, never a
 * stale literal. Returns a deduped, ROLES-ordered list; empty if no route
 * requires the scope.
 *
 * Used by the MCP tool-call approver push-to-pull notification
 * (docs/specs/nexus-mcp-approval-audit-v0.md §9 (b)) to resolve the recipient
 * roles for `mcp:approve_tool_call`.
 */
export function rolesWithScope(scope: string): Role[] {
  const matchedRoutes: string[] = [];
  for (const [route, perms] of Object.entries(DASHBOARD_ROUTE_PERMISSIONS)) {
    if (perms.includes(scope as Permission)) matchedRoutes.push(route);
  }
  const set = new Set<Role>();
  for (const route of matchedRoutes) {
    for (const role of DASHBOARD_ROUTE_ROLES[route] ?? []) set.add(role);
  }
  // Stable, catalog-ordered output.
  return ROLES.filter((r) => set.has(r));
}

// Re-export sub-modules
export * from "./actor.js";
export * from "./middleware.js";
export * from "./tenant.js";
export * from "./permissions.js";
